import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, ShieldCheck, BadgeEuro, Clock, Scale, Check, X, Calculator, Search, Send } from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import SEO from "../components/SEO";
import { apiClient } from "../lib/api";
import AboutTeam from "../components/AboutTeam";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const H = { fontFamily: "Quicksand, sans-serif" };

const FAQ = [
  { q: "Pourquoi passer par un comparateur de conciergeries ?", a: "Les conciergeries Airbnb n'ont ni les mêmes tarifs, ni les mêmes services, ni les mêmes zones d'intervention. Au lieu d'en appeler plusieurs, vous décrivez votre bien une seule fois et nous identifions la conciergerie la plus adaptée à votre commune et à vos besoins." },
  { q: "Combien ça coûte au propriétaire ?", a: "0 €. Notre mise en relation est entièrement gratuite pour les propriétaires, sans engagement : ce sont les conciergeries partenaires qui financent le service. Vous ne payez que la conciergerie que vous choisissez, si sa proposition vous convient." },
  { q: "Que devient ma demande de devis ?", a: "Un membre de notre équipe vous rappelle par téléphone pour bien comprendre votre bien, vos besoins et les services souhaités. Nous vous orientons ensuite vers la conciergerie la plus adaptée, qui vous envoie une proposition personnalisée. Nous sommes joignables 7j/7 de 10h à 20h par email." },
  { q: "Mes coordonnées sont-elles partagées ?", a: "Uniquement avec la conciergerie sélectionnée pour votre bien. Elles ne sont jamais revendues ni diffusées publiquement." },
  { q: "Quelles communes couvrez-vous ?", a: "Toute la Bretagne, de la plus grande ville au plus petit village : Côtes-d'Armor, Finistère, Ille-et-Vilaine et Morbihan, ainsi que la baie du Mont-Saint-Michel." },
  { q: "Combien coûte une conciergerie Airbnb ?", a: "En général entre 18% et 25% TTC des revenus locatifs, selon les services inclus (ménage, linge, accueil, annonce, prix dynamiques). Le devis vous donne le tarif exact pour votre bien." },
];

export default function HomePage() {
  const [cities, setCities] = useState([]);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    apiClient.get("/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  const featured = cities.filter((c) => c.featured);
  const nz = (x) => x.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const match = q.length > 1 ? cities.filter((c) => nz(c.name).includes(nz(q))).sort((a, b) => nz(a.name).indexOf(nz(q)) - nz(b.name).indexOf(nz(q))).slice(0, 6) : [];
  const faqLd = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };

  return (
    <PublicLayout>
      <SEO
        title="Comparateur de conciergerie Airbnb en Bretagne : devis gratuit"
        description="Le comparateur indépendant de conciergeries Airbnb : Saint-Malo, Dinard, Cancale, Dinan, Rennes, Saint-Brieuc, Granville et 1308 communes. Une seule demande de devis gratuite, la conciergerie adaptée à votre bien."
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* HERO */}
      <section className="px-3 md:px-6 pt-2" data-testid="hero">
        <div className="relative rounded-[2rem] overflow-hidden min-h-[560px] md:min-h-[620px] flex items-center">
          <img src="/api/static/site/hero-saint-malo.webp" alt="Saint-Malo, conciergerie Airbnb en Bretagne" className="absolute inset-0 w-full h-full object-cover" data-testid="hero-image" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/85 via-primary/55 to-primary/10" />
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative container-tm py-16 text-white">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur text-xs font-bold uppercase tracking-[0.12em]"><span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--ocean))] animate-pulse" /> Comparateur indépendant · {cities.length || 1308} communes</span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] max-w-3xl" style={H}>
              Trouvez la bonne <span className="text-[hsl(var(--accent))] bg-white px-3 rounded-2xl leading-[1.3] box-decoration-clone">conciergerie Airbnb</span> en Bretagne
            </h1>
            <p className="mt-6 text-lg text-white/90 leading-relaxed max-w-xl">
              De la ville au plus petit village. Décrivez votre bien une seule fois : nous vous rappelons et vous orientons vers la conciergerie adaptée.
            </p>
            <div className="mt-8 bg-white rounded-3xl p-2.5 flex flex-col sm:flex-row gap-2 max-w-2xl relative shadow-2xl text-foreground" data-testid="hero-search">
              <div className="flex-1 relative">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Votre commune (ex : Dinard)"
                  className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-transparent focus:outline-none" data-testid="hero-city-input" />
                {match.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-2 surface-card p-1.5 z-20" data-testid="hero-city-suggestions">
                    {match.map((c) => (
                      <button key={c.slug} onClick={() => navigate(`/devis?ville=${c.slug}`)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-secondary text-sm flex justify-between" data-testid={`hero-suggest-${c.slug}`}>
                        <span className="font-medium">{c.name}</span><span className="text-muted-foreground text-xs">{c.department}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => navigate(match[0] ? `/devis?ville=${match[0].slug}` : "/devis")} className="btn-accent" data-testid="hero-cta-devis">
                Comparer <ArrowRight size={16} />
              </button>
            </div>
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/90">
              {["0 € pour le propriétaire", "Sans engagement", "Rappel par téléphone"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><Check size={14} className="text-[hsl(var(--ocean))]" strokeWidth={3} /> {t}</span>
              ))}
            </div>
            <button onClick={() => navigate("/simulateur")} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-[hsl(var(--ocean))] transition-colors" data-testid="hero-cta-simulateur">
              <Calculator size={15} /> Ou estimez d&apos;abord vos revenus Airbnb
            </button>
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="py-12 section-beige mx-3 md:mx-6" data-testid="trust-bar">
        <div className="container-tm grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: BadgeEuro, t: "0 € pour vous", d: "Service gratuit pour les propriétaires" }, { icon: Scale, t: "Conciergeries triées sur le volet", d: "Uniquement nos partenaires, sélectionnés un par un" },
            { icon: MapPin, t: `${cities.length || 1308} communes`, d: "Bretagne et Normandie" },
            { icon: Clock, t: "7j/7, 10h-20h", d: "Une équipe qui vous rappelle" },
                      ].map((b) => (
            <div key={b.t} className="flex items-start gap-3">
              <span className="w-11 h-11 rounded-full bg-white text-[hsl(var(--ocean))] flex items-center justify-center shrink-0 shadow-sm"><b.icon size={18} /></span>
              <div><div className="font-semibold" style={H}>{b.t}</div><div className="text-sm text-muted-foreground">{b.d}</div></div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-28">
        <div className="container-tm">
          <div className="max-w-2xl">
            <span className="pill">Méthode</span>
            <h2 className="h-section mt-4">Comment fonctionne le comparateur</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-px bg-border rounded-2xl overflow-hidden border border-border">
            {[
              { n: "01", t: "Vous décrivez votre bien", d: "Commune, type de bien, capacité, services souhaités. 5 étapes, 2 minutes." },
              { n: "02", t: "Nous vous rappelons", d: "Un membre de l'équipe vous appelle pour comprendre vos besoins et les services souhaités, puis compare les conciergeries de votre secteur." },
              { n: "03", t: "On vous oriente", d: "Nous vous mettons en relation avec la bonne conciergerie, qui vous envoie une proposition personnalisée. Vous décidez librement." },
            ].map((s, i) => (
              <motion.div key={s.n} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="bg-white p-8 md:p-10" data-testid={`step-${s.n}`}>
                <div className="text-sm font-bold text-accent tracking-[0.15em]">{s.n}</div>
                <h3 className="h-card mt-4">{s.t}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
          <button onClick={() => navigate("/devis")} className="btn-primary mt-10" data-testid="how-cta-devis">Commencer ma demande <ArrowRight size={16} /></button>
        </div>
      </section>

      <AboutTeam />

      {/* COMPARISON TABLE */}
      <section className="py-20 md:py-28 bg-primary text-white rounded-[2rem] mx-3 md:mx-6" data-testid="home-comparator">
        <div className="container-tm grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="pill bg-white/10 text-white border-white/15">Pourquoi nous</span>
            <h2 className="h-section mt-4 text-white">Chercher seul ou <span className="text-[hsl(var(--ocean))]">comparer</span> ?</h2>
            <p className="mt-5 text-white/70 leading-relaxed">Trouver la bonne conciergerie prend du temps : appels, rendez-vous, devis difficiles à comparer. Le comparateur fait ce travail pour vous.</p>
            <button onClick={() => navigate("/devis")} className="btn-accent mt-8" data-testid="home-quote-cta"><Send size={16} /> Demander mon devis gratuit</button>
          </div>
          <div className="lg:col-span-7 rounded-2xl overflow-hidden border border-white/10">
            <div className="grid grid-cols-3 bg-white/5 text-xs uppercase tracking-[0.15em] font-semibold">
              <div className="p-4" /><div className="p-4 text-white/60">Seul</div><div className="p-4 text-[hsl(var(--ocean))]">Bretagne Conciergerie Comparateur</div>
            </div>
            {[
              ["Démarches", "Appeler plusieurs conciergeries", "Une seule demande"],
              ["Temps passé", "Plusieurs jours", "2 minutes"],
              ["Zone couverte", "À vérifier une par une", "Conciergerie de votre secteur"],
              ["Coût", "Gratuit", "Gratuit"],
              ["Engagement", "Variable", "Aucun"],
            ].map(([k, a, b]) => (
              <div key={k} className="grid grid-cols-3 border-t border-white/10 text-sm">
                <div className="p-4 font-semibold">{k}</div>
                <div className="p-4 text-white/60 flex items-center gap-2"><X size={13} className="shrink-0" /> {a}</div>
                <div className="p-4 flex items-center gap-2"><Check size={13} className="text-[hsl(var(--ocean))] shrink-0" strokeWidth={3} /> {b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNES */}
      <section className="py-20 md:py-28">
        <div className="container-tm">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div className="max-w-2xl">
              <span className="pill" data-testid="pill-villes">{cities.length || 1308} communes analysées</span>
              <h2 className="h-section mt-4">Conciergerie et rentabilité, <span className="text-accent">commune par commune</span></h2>
            </div>
            <Link to="/villes" className="btn-ghost self-start md:self-end" data-testid="all-cities-link">Toutes les communes <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {featured.slice(0, 8).map((c, idx) => (
              <motion.div key={c.slug} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (idx % 4) * 0.06 }}>
                <Link to={`/villes/${c.slug}`} className="block group" data-testid={`city-card-${c.slug}`}>
                  <div className="relative aspect-[4/5] overflow-hidden blob bg-secondary">
                    <img src={c.image} alt={`Conciergerie Airbnb ${c.name}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/10 to-transparent" />
                    <div className="absolute bottom-0 inset-x-0 p-4 md:p-5 text-white">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/70">{c.department}</div>
                      <h3 className="text-xl md:text-2xl font-bold mt-1" style={H}>{c.name}</h3>
                      <div className="mt-2 flex gap-3 text-xs text-white/85"><span>{c.occupancy_rate}% occup.</span><span>{c.adr} €/nuit</span></div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SIMULATOR TEASER */}
      <section className="pb-20 md:pb-28">
        <div className="container-tm">
          <div className="surface-card p-8 md:p-12 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="pill">Simulateur gratuit</span>
              <h2 className="h-section mt-4">Combien peut rapporter votre bien ?</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">Estimation instantanée selon la commune, la taille, la distance à la plage et les atouts du logement.</p>
              <button onClick={() => navigate("/simulateur")} className="btn-primary mt-8" data-testid="final-cta-simulateur"><Calculator size={16} /> Lancer mon estimation</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {featured.slice(0, 4).map((c) => (
                <Link key={c.slug} to={`/villes/${c.slug}`} className="rounded-xl border border-border p-5 hover:border-accent transition-colors" data-testid={`teaser-${c.slug}`}>
                  <div className="text-xs text-muted-foreground">T3 à {c.name}</div>
                  <div className="font-bold text-lg mt-1" style={H}>{c.adr} €<span className="text-xs text-muted-foreground font-normal"> /nuit</span></div>
                  <div className="text-xs text-accent font-semibold mt-1">{c.occupancy_rate}% d&apos;occupation</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-20 md:pb-28" data-testid="home-faq">
        <div className="container-tm grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <span className="pill">FAQ</span>
            <h2 className="h-section mt-4">Questions fréquentes</h2>
          </div>
          <Accordion type="single" collapsible className="lg:col-span-8">
            {FAQ.map((f, i) => (
              <AccordionItem key={f.q} value={`f${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left text-base md:text-lg font-semibold" style={H}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed text-base">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="pb-20">
        <div className="container-tm">
          <div className="blob-alt bg-accent text-accent-foreground p-10 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div>
              <h2 className="h-section text-white">Votre conciergerie, en une seule demande.</h2>
              <p className="mt-3 text-white/85">Gratuit, sans engagement. Joignables 7j/7 de 10h à 20h.</p>
            </div>
            <button onClick={() => navigate("/devis")} className="btn-sun px-7 py-4 shrink-0" data-testid="final-cta-devis">
              Demander mon devis <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
